import { useEffect, useState } from 'react';
import { machinesSocket } from '../services/machinesLive';

export type LiveOverlay = Record<
  string,
  { [field: string]: unknown; status?: string; timestamp?: string }
>;

export function useMachinesLive() {
  const [liveOverlay, setLiveOverlay] = useState<LiveOverlay>({});
  const [connected, setConnected] = useState(machinesSocket.connected);

  useEffect(() => {
    if (!machinesSocket.connected) machinesSocket.connect();

    const onConnect = () => setConnected(true);
    const onDisconnect = () => setConnected(false);

    setConnected(machinesSocket.connected);
    machinesSocket.on('connect', onConnect);
    machinesSocket.on('disconnect', onDisconnect);

    const onTelemetry = (payload: {
      machineId: string;
      field: string;
      value: unknown;
      status?: string;
      timestamp?: string;
    }) => {
      setLiveOverlay((prev) => ({
        ...prev,
        [payload.machineId]: {
          ...(prev[payload.machineId] ?? {}),
          [payload.field]: payload.value,
          status: payload.status ?? prev[payload.machineId]?.status,
          timestamp: payload.timestamp ?? new Date().toISOString(),
        },
      }));
    };

    const onStatus = (payload: { machineId: string; status: string }) => {
      setLiveOverlay((prev) => ({
        ...prev,
        [payload.machineId]: {
          ...(prev[payload.machineId] ?? {}),
          status: payload.status,
          timestamp: new Date().toISOString(),
        },
      }));
    };

    const onAlarm = (payload: {
      machineId: string;
      code?: string;
      message?: string;
    }) => {
      setLiveOverlay((prev) => ({
        ...prev,
        [payload.machineId]: {
          ...(prev[payload.machineId] ?? {}),
          errorMessage: payload.message ?? payload.code,
          timestamp: new Date().toISOString(),
        },
      }));
    };

    machinesSocket.on('machine.telemetry', onTelemetry);
    machinesSocket.on('machine.status', onStatus);
    machinesSocket.on('machine.alarm', onAlarm);

    return () => {
      machinesSocket.off('connect', onConnect);
      machinesSocket.off('disconnect', onDisconnect);
      machinesSocket.off('machine.telemetry', onTelemetry);
      machinesSocket.off('machine.status', onStatus);
      machinesSocket.off('machine.alarm', onAlarm);
      machinesSocket.disconnect();
    };
  }, []);

  return { liveOverlay, connected };
}
